import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';

import { useDebounce } from '../../../hooks';

import { StyledFormControl } from './styles';
import { VpIcon } from '../../components';

// eslint-disable-next-line react/prop-types
const StarRating = ({ starId, value, onRatingChange }) => (
  <VpIcon
    icon="VpStar"
    size="30px"
    sx={{ cursor: 'pointer', color: value >= starId ? '#FFCB46' : '#D9D9D9' }}
    onClick={() => onRatingChange(starId)}
  />
);

export const VpHookFormRatingInput = ({
  name = '',
  defaultValue = 0,
  required = false,
  smallLabel = false,
  hidden = false,
  isStandardInput = false,
  label = '',
  range = [1, 5],
  disableHelperText = false,
  helperText = '',
  onChange = null,
  onChangeDebounce = null,
  onChangeDebounceTimeout = 0,
  returnString = false,
  rules = {},
  ...rest
}) => {
  const id = `vp-hook-input-${name}`;

  const vpOnChangeDebounce = onChangeDebounceTimeout
    ? useDebounce((val) => {
        onChangeDebounce(val);
      }, onChangeDebounceTimeout)
    : onChangeDebounce;

  const { setValue } = useFormContext();
  const {
    field: { onChange: onControllerChange, value },
    fieldState: { error }
  } = useController({
    name,
    rules: { required, ...rules },
    defaultValue
  });

  const handleRatingChange = (newRating) => {
    const updatedRating = returnString ? `${newRating}` : newRating;

    setValue(name, updatedRating);
    onControllerChange(updatedRating);
    onChange && onChange(updatedRating);
    vpOnChangeDebounce && vpOnChangeDebounce(updatedRating);
  };

  const errorMessage = error?.message;
  // const defaultHelperText = disableHelperText ? '' : <span>&nbsp;</span>;
  const helperTextMessage = disableHelperText
    ? null
    : errorMessage || helperText || <span>&nbsp;</span>;

  const startRange = range[0];
  const endRange = range[1];
  const stars = Array.from({ length: endRange - startRange + 1 }, (_, i) => startRange + i);

  return (
    <StyledFormControl
      hidden={hidden}
      fullWidth
      variant={isStandardInput ? 'standard' : 'outlined'}
    >
      {label && (
        <Typography htmlFor={id} variant={smallLabel ? 'inputLabelSmall' : 'inputLabel'}>
          {label}
        </Typography>
      )}
      <Stack direction="row" gap="5px">
        {stars.map((starId) => (
          <StarRating
            id={id}
            key={starId}
            starId={starId}
            value={value}
            onRatingChange={handleRatingChange}
            {...rest}
          />
        ))}
      </Stack>
      {helperTextMessage && (
        <FormHelperText error={!!error} id={`${id}-helper-text`}>
          {helperTextMessage}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
};

VpHookFormRatingInput.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.number,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rules: PropTypes.object,
  range: PropTypes.array,
  returnString: PropTypes.bool,
  // CHECK
  disabled: PropTypes.bool,
  maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isEmail: PropTypes.bool,
  isNumber: PropTypes.bool,
  onChange: PropTypes.func,
  onChangeDebounce: PropTypes.func,
  onChangeDebounceTimeout: PropTypes.number,
  isLoading: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.string,
  disableHelperText: PropTypes.bool,
  helperText: PropTypes.string,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  smallLabel: PropTypes.bool,
  hidden: PropTypes.bool,
  isUppercase: PropTypes.bool,
  isStandardInput: PropTypes.bool,
  boldValue: PropTypes.bool,
  fallbackDefaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  includeDisplayInput: PropTypes.bool
};

export default VpHookFormRatingInput;



// WEBPACK FOOTER //
// ./src/vp-ui/inputs/VpHookFormRatingInput/VpHookFormRatingInput.jsx